const PAGE = {
  width: 842,
  height: 595,
  margin_x: 28,
  margin_top: 34,
  margin_bottom: 32,
};

const COLUMNS = [
  { key: 'time', label: 'Time', width: 76 },
  { key: 'actor', label: 'Actor', width: 76 },
  { key: 'actor_role', label: 'Role', width: 58 },
  { key: 'status', label: 'Status', width: 46 },
  { key: 'source', label: 'Source', width: 54 },
  { key: 'event', label: 'Event', width: 58 },
  { key: 'action', label: 'Action', width: 80 },
  { key: 'target', label: 'Case / Target', width: 80 },
  { key: 'details', label: 'Details', width: 252 },
];

const FONT = {
  title: 16,
  meta: 8,
  header: 7,
  body: 7,
};

export function audit_rows_to_pdf(rows = [], options = {}) {
  const pages = paginate_rows(rows);
  const streams = pages.map((page_rows, index) =>
    page_stream(page_rows, {
      ...options,
      page_number: index + 1,
      page_count: pages.length,
      first_page: index === 0,
    })
  );
  return pdf_document(streams);
}

function paginate_rows(rows = []) {
  const pages = [];
  let page = [];
  let y = PAGE.height - PAGE.margin_top - 78;

  rows.forEach((row) => {
    const height = row_height(row);
    if (page.length && y - height < PAGE.margin_bottom) {
      pages.push(page);
      page = [];
      y = PAGE.height - PAGE.margin_top - 78;
    }

    page.push(row);
    y -= height;
  });

  pages.push(page);
  return pages;
}

function page_stream(rows = [], options = {}) {
  const lines = [];
  let y = PAGE.height - PAGE.margin_top;

  if (options.first_page) {
    lines.push(text_line('System Audit Log', PAGE.margin_x, y, FONT.title, true));
    y -= 16;
    lines.push(text_line(`Generated ${options.generated_at || ''} - ${options.row_count || rows.length} filtered events`, PAGE.margin_x, y, FONT.meta));
    y -= 22;
  } else {
    lines.push(text_line('System Audit Log', PAGE.margin_x, y, 11, true));
    y -= 26;
  }

  lines.push(...table_header_lines(y));
  y -= 22;

  rows.forEach((row, row_index) => {
    const height = row_height(row);
    lines.push(...row_background_lines(y, height, row_index));
    lines.push(...table_row_lines(row, y - 12));
    y -= height;
  });

  lines.push(text_line(`Page ${options.page_number} of ${options.page_count}`, PAGE.width - 92, 18, 7));
  return lines.join('\n');
}

function table_header_lines(y) {
  const lines = [rect_line(PAGE.margin_x, y - 9, table_width(), 18, '12 81 68')];
  let x = PAGE.margin_x + 4;

  COLUMNS.forEach((column) => {
    lines.push(text_line(column.label, x, y - 2, FONT.header, true, 'white'));
    x += column.width;
  });

  return lines;
}

function row_background_lines(y, height, index) {
  if (index % 2 !== 1) return [];
  return [rect_line(PAGE.margin_x, y - height + 3, table_width(), height, '247 250 249')];
}

function table_row_lines(row = {}, y) {
  const lines = [];
  let x = PAGE.margin_x + 4;

  COLUMNS.forEach((column) => {
    const cell_lines = wrapped_lines(row[column.key], max_chars(column.width));
    cell_lines.forEach((line, index) => {
      lines.push(text_line(line, x, y - (index * 9), FONT.body));
    });
    x += column.width;
  });

  return lines;
}

function row_height(row = {}) {
  const max_lines = Math.max(
    1,
    ...COLUMNS.map((column) => wrapped_lines(row[column.key], max_chars(column.width)).length)
  );
  return Math.max(22, 12 + (max_lines * 9));
}

function table_width() {
  return COLUMNS.reduce((sum, column) => sum + column.width, 0);
}

function max_chars(width) {
  return Math.max(6, Math.floor(width / 4.2));
}

function wrapped_lines(value = '', max = 24) {
  const words = normalize_pdf_text(value).split(/\s+/).filter(Boolean);
  if (!words.length) return [''];

  const lines = [''];

  words.forEach((word) => {
    const last = lines[lines.length - 1] || '';
    const next = last ? `${last} ${word}` : word;
    if (next.length <= max) {
      lines[lines.length - 1] = next;
      return;
    }
    lines.push(...split_long_word(word, max));
  });

  return lines.filter(Boolean);
}

function split_long_word(word = '', max = 24) {
  if (word.length <= max) return [word];
  const chunks = [];
  for (let index = 0; index < word.length; index += max) {
    chunks.push(word.slice(index, index + max));
  }
  return chunks;
}

function text_line(text = '', x = 40, y = 720, size = 9, bold = false, color = '') {
  return [
    color === 'white' ? '1 1 1 rg' : '0 0 0 rg',
    `BT /${bold ? 'F2' : 'F1'} ${size} Tf`,
    `${x} ${y} Td`,
    `(${pdf_escape(text)}) Tj ET`,
  ].join(' ');
}

function rect_line(x, y, width, height, color = '247 250 249') {
  return `${pdf_rgb(color)} rg ${x} ${y} ${width} ${height} re f`;
}

function pdf_document(streams = []) {
  const page_count = streams.length || 1;
  const page_ids = streams.map((_, index) => 4 + (index * 2));
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    `<< /Type /Pages /Kids [${page_ids.map((id) => `${id} 0 R`).join(' ')}] /Count ${page_count} >>`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ];

  streams.forEach((stream, index) => {
    const page_id = 4 + (index * 2);
    const content_id = page_id + 1;
    objects.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE.width} ${PAGE.height}] /Resources << /Font << /F1 3 0 R /F2 << /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >> >> >> /Contents ${content_id} 0 R >>`);
    objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });

  return build_pdf(objects);
}

function build_pdf(objects = []) {
  const parts = ['%PDF-1.4\n'];
  const offsets = [];
  objects.forEach((object, index) => {
    offsets.push(parts.join('').length);
    parts.push(`${index + 1} 0 obj\n${object}\nendobj\n`);
  });
  add_xref(parts, offsets, objects.length);
  return parts.join('');
}

function add_xref(parts, offsets = [], object_count = 0) {
  const xref_offset = parts.join('').length;
  parts.push(`xref\n0 ${object_count + 1}\n0000000000 65535 f \n`);
  offsets.forEach((offset) => {
    parts.push(`${String(offset).padStart(10, '0')} 00000 n \n`);
  });
  parts.push(`trailer << /Size ${object_count + 1} /Root 1 0 R >>\nstartxref\n${xref_offset}\n%%EOF`);
}

function pdf_escape(value = '') {
  return normalize_pdf_text(value).replace(/[\\()]/g, '\\$&');
}

function normalize_pdf_text(value = '') {
  return String(value ?? '')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[^\x20-\x7E]/g, '');
}

function pdf_rgb(value = '') {
  return value
    .split(/\s+/)
    .map((channel) => Number(channel) / 255)
    .map((channel) => Number.isFinite(channel) ? channel.toFixed(3) : '0')
    .join(' ');
}
